import React, { useCallback, useMemo, useRef,useState } from 'react';

import PropTypes from 'prop-types';
import { Info } from 'react-feather';
import { Field,Form, FormSpy } from 'react-final-form';
import { toast } from 'react-toastify';

import CouponCategoryField from 'containers/CouponCategoryField/CouponCategoryField';

import styles from './Step1_CombinedSetup.module.scss';

const GOALS = [
  {
    key: 'attract_new_customers',
    label: 'Attract New Customers',
    blurb: 'Expand your reach and bring in first-time visitors.',
    info:
      'Create a solution designed to expand your reach and grow your customer base by attracting first-time visitors to your business.',
  },
  {
    key: 'increase_sales',
    label: 'Increase Sales',
    blurb: 'Boost demand for a particular item or service.',
    info:
      'Create a solution designed to drive more purchases of a specific product or service by making it irresistible.',
  },
  {
    key: 'fill_slow_periods',
    label: 'Fill Slow Periods',
    blurb: 'Attract customers during quieter hours or days.',
    info:
      'Create a solution designed to bring customers in during low-traffic periods to stabilize revenue.',
  },
];

const IMPACTS = [
  {
    key: 'steady_growth',
    label: 'Steady Growth',
    blurb: 'Consistent performance without huge discounts.',
  },
  {
    key: 'quick_boost',
    label: 'Quick Boost',
    blurb: 'A strong push to drive demand fast.',
  },
  {
    key: 'maximum_impact',
    label: 'Maximum Impact',
    blurb: 'The biggest response — best for aggressive campaigns.',
  },
];

const ITEM_TYPES = [
  {
    key: 'individual',
    label: 'Individual Item',
    hint: 'Promote one hero product/service.',
  },
  {
    key: 'category',
    label: 'A Line of Items',
    hint: 'Promote a category or collection.',
  },
  { key: 'all', label: 'Storewide', hint: 'Applies to all products/services.' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TIME_BLOCKS = [
  'Morning (8–12)',
  'Afternoon (1–6)',
  'Evening (6–9)',
  'All day',
];

function InfoIcon({ text }) {
  return (
    <span className={styles.infoWrap} title={text} aria-label={text}>
      <Info size={14} />
    </span>
  );
}
InfoIcon.propTypes = { text: PropTypes.string.isRequired };

export default function Step1_CombinedSetup({
  entryPoint,
  draft,
  onGenerate,
  onSkipForNow,
  onPreviewChange,
  onGenerateTitle,
  onGenerateDescription,
}) {
  const isEntryA = entryPoint === 'A';

  // ---- LOCAL (UI) STATE ONLY ----
  const [goalKey, setGoalKey] = useState(draft.goalKey || '');
  const [impactKey, setImpactKey] = useState(draft.impactKey || '');
  const [itemType, setItemType] = useState(draft.offering?.itemType || '');

  const [title, setTitle] = useState(draft.offering?.itemName || '');
  const [description, setDescription] = useState(
    draft.offering?.itemDescription || '',
  );

  const [price, setPrice] = useState(draft.offering?.price || '');
  const [minSpend, setMinSpend] = useState(draft.offering?.minSpend || '');
  const [purchaseQty, setPurchaseQty] = useState(
    draft.offering?.purchaseQty || '',
  );
  const [slowDays, setSlowDays] = useState(draft.offering?.slowDays || []);
  const [slowTimes, setSlowTimes] = useState(draft.offering?.slowTimes || []);

  const [isGenTitleLoading, setIsGenTitleLoading] = useState(false);
  const [isGenDescLoading, setIsGenDescLoading] = useState(false);

  const didFirstPreviewPush = useRef(false);

  const goalObj = useMemo(() => GOALS.find(g => g.key === goalKey), [goalKey]);

  const section2Title = useMemo(() => {
    if (!goalKey) return '';
    if (goalKey === 'attract_new_customers')
      return "Your Best Foot Forward: What's Your Flagship Offering?";
    if (goalKey === 'increase_sales')
      return "Boost Your Bottom Line: What's Your High-Earning Product/Service?";
    if (goalKey === 'fill_slow_periods')
      return 'Optimize Your Hours: When Are Your Slow Periods?';
    return "What's Your Core Offering?";
  }, [goalKey]);

  const section2Sub = useMemo(() => {
    if (!goalKey) return '';
    if (goalKey === 'attract_new_customers') {
      return 'Tell us about the product or service that best represents your business — the one that will make new customers fall in love!';
    }
    if (goalKey === 'increase_sales') {
      return 'Let’s identify the item or service that, when promoted, will significantly increase your revenue.';
    }
    if (goalKey === 'fill_slow_periods') {
      return 'Help us understand when you need a boost during quieter times (optional).';
    }
    return 'Help us understand your core product or service.';
  }, [goalKey]);

  const hasOfferingBasics = useMemo(() => {
    if (!goalKey) return false;
    if (!itemType) return false;
    return title.trim().length > 1;
  }, [goalKey, itemType, title]);

  const canRevealImpact = useMemo(() => hasOfferingBasics, [hasOfferingBasics]);

  const canGenerate = useMemo(
    () => Boolean(goalKey && hasOfferingBasics && impactKey),
    [goalKey, hasOfferingBasics, impactKey],
  );

  const canGenerateTitle = useMemo(() => Boolean(goalKey && itemType), [
    goalKey,
    itemType,
  ]);

  const canGenerateDesc = useMemo(
    () => Boolean(goalKey && itemType && title.trim().length > 1),
    [goalKey, itemType, title],
  );

  const withTimeout = (promise, ms = 20000) =>
    Promise.race([
      promise,
      new Promise(resolve => setTimeout(() => resolve(null), ms)),
    ]);

  const toggleSlowDay = d => {
    const s = new Set(slowDays);
    s.has(d) ? s.delete(d) : s.add(d);
    setSlowDays(Array.from(s));
  };

  const toggleSlowTime = t => {
    const s = new Set(slowTimes);
    s.has(t) ? s.delete(t) : s.add(t);
    setSlowTimes(Array.from(s));
  };

  const buildOfferingFrom = useCallback(
    (formValues, overrides = {}) => {
      const selectedCategoryObj = formValues?.category || null;
      const selectedSubcategories = Array.isArray(formValues?.subcategories)
        ? formValues.subcategories
        : [];

      const nextTitle =
        typeof overrides.itemName === 'string' ? overrides.itemName : title;
      const nextDesc =
        typeof overrides.itemDescription === 'string'
          ? overrides.itemDescription
          : description;

      return {
        itemType,
        itemName: (nextTitle || '').trim(),
        itemDescription: (nextDesc || '').trim(),
        price,
        minSpend,
        purchaseQty,
        slowDays,
        slowTimes,

        category: selectedCategoryObj,
        categoryId: selectedCategoryObj?.id || null,
        categoryName: selectedCategoryObj?.name || null,

        subcategories: selectedSubcategories,
        subcategoryId: selectedSubcategories?.[0] || null,

        categoryLabel: (selectedCategoryObj?.name || '').trim() || '',
      };
    },
    [
      itemType,
      title,
      description,
      price,
      minSpend,
      purchaseQty,
      slowDays,
      slowTimes,
    ],
  );

  const buildPayload = useCallback(
    (formValues, offeringOverrides = {}) => ({
      goalKey,
      impactKey,
      offering: buildOfferingFrom(formValues, offeringOverrides),
    }),
    [goalKey, impactKey, buildOfferingFrom],
  );

  const pushPreview = useCallback(
    (formValues, offeringOverrides = {}) => {
      if (typeof onPreviewChange !== 'function') return;
      const payload = buildPayload(formValues, offeringOverrides);
      setTimeout(() => onPreviewChange(payload), 0);
    },
    [onPreviewChange, buildPayload],
  );

  const handleSubmit = useCallback(
    values => {
      if (!canGenerate) return;
      onGenerate(buildPayload(values));
    },
    [canGenerate, onGenerate, buildPayload],
  );

  const showLoadingToast = useCallback(msg => {
    if (typeof toast.loading === 'function') return toast.loading(msg);
    return toast(msg, {
      isLoading: true,
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
    });
  }, []);

  const safeToastUpdate = useCallback((id, opts) => {
    try {
      if (id != null) toast.update(id, opts);
    } catch (e) {
      // ignore toast update failures; never block generation
    }
  }, []);

  const handleGenerateTitle = useCallback(
    async values => {
      if (!canGenerateTitle || isGenTitleLoading) {
        if (!canGenerateTitle) toast.info('Choose a goal + item type first.');
        return;
      }
      if (typeof onGenerateTitle !== 'function') return;

      setIsGenTitleLoading(true);

      let toastId;
      try {
        toastId = showLoadingToast('Generating title…');

        const text = await withTimeout(
          onGenerateTitle(buildPayload(values)),
          20000,
        );
        const next = typeof text === 'string' ? text.trim() : '';

        if (!next) {
          safeToastUpdate(toastId, {
            render: 'Could not generate a title. Try again.',
            type: 'warning',
            isLoading: false,
            autoClose: 2200,
          });
          return;
        }

        setTitle(next);
        pushPreview(values, { itemName: next });

        safeToastUpdate(toastId, {
          render: 'Title generated!',
          type: 'success',
          isLoading: false,
          autoClose: 1400,
        });
      } catch (e) {
        safeToastUpdate(toastId, {
          render: 'Title generation failed. Try again.',
          type: 'error',
          isLoading: false,
          autoClose: 2200,
        });
      } finally {
        setIsGenTitleLoading(false);
      }
    },
    [
      canGenerateTitle,
      isGenTitleLoading,
      onGenerateTitle,
      showLoadingToast,
      safeToastUpdate,
      withTimeout,
      buildPayload,
      pushPreview,
    ],
  );

  const handleGenerateDescription = useCallback(
    async values => {
      if (!canGenerateDesc || isGenDescLoading) {
        if (!canGenerateDesc)
          toast.info('Enter a title first (and choose goal + item type).');
        return;
      }
      if (typeof onGenerateDescription !== 'function') return;

      setIsGenDescLoading(true);

      let toastId;
      try {
        toastId = showLoadingToast('Generating description…');

        const text = await withTimeout(
          onGenerateDescription(buildPayload(values)),
          20000,
        );
        const next = typeof text === 'string' ? text.trim() : '';

        if (!next) {
          safeToastUpdate(toastId, {
            render: 'Could not generate a description. Try again.',
            type: 'warning',
            isLoading: false,
            autoClose: 2200,
          });
          return;
        }

        setDescription(next);
        pushPreview(values, { itemDescription: next });

        safeToastUpdate(toastId, {
          render: 'Description generated!',
          type: 'success',
          isLoading: false,
          autoClose: 1400,
        });
      } catch (e) {
        safeToastUpdate(toastId, {
          render: 'Description generation failed. Try again.',
          type: 'error',
          isLoading: false,
          autoClose: 2200,
        });
      } finally {
        setIsGenDescLoading(false);
      }
    },
    [
      canGenerateDesc,
      isGenDescLoading,
      onGenerateDescription,
      showLoadingToast,
      safeToastUpdate,
      withTimeout,
      buildPayload,
      pushPreview,
    ],
  );

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        category: draft.offering?.category || undefined,
        subcategories: Array.isArray(draft.offering?.subcategories)
          ? draft.offering.subcategories
          : [],
      }}
      keepDirtyOnReinitialize
      enableReinitialize={false}
    >
      {({ handleSubmit: submitForm, values }) => (
        <div className={styles.page}>
          <FormSpy
            subscription={{ values: true }}
            onChange={({ values: v }) => {
              if (!didFirstPreviewPush.current) {
                didFirstPreviewPush.current = true;
                pushPreview(v);
                return;
              }
              pushPreview(v);
            }}
          />

          {isEntryA && onSkipForNow && (
            <section className={styles.panel}>
              <div className={styles.panelHead}>
                <div className={styles.panelTitle}>
                  Time to Create Your First Coupon!
                </div>
                <div className={styles.panelSub}>
                  Your profile is ready! Customers are looking for deals — don’t
                  let them see a blank profile. Start by choosing a goal for
                  your coupon, or click “I’ll do it later”.
                </div>
              </div>

              <div className={styles.rowRight}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={onSkipForNow}
                >
                  I’ll do it later
                </button>
              </div>
            </section>
          )}

          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <div className={styles.panelTitle}>What’s your main goal?</div>
              <div className={styles.panelSub}>
                {isEntryA
                  ? 'Start by choosing a goal for your first coupon.'
                  : 'Let’s find the perfect solution to help your business grow. What are you hoping to achieve?'}
              </div>
            </div>

            <div
              className={styles.radioGrid}
              role="radiogroup"
              aria-label="Goals"
            >
              {GOALS.map((g, idx) => {
                const active = goalKey === g.key;
                return (
                  <button
                    key={g.key}
                    type="button"
                    className={`${styles.radioCard} ${
                      active ? styles.active : ''
                    }`}
                    role="radio"
                    aria-checked={active}
                    onClick={() => setGoalKey(g.key)}
                    style={{ '--stagger': `${idx * 60}ms` }}
                  >
                    <div className={styles.cardTop}>
                      <div className={styles.cardLabel}>{g.label}</div>
                      <InfoIcon text={g.info} />
                    </div>
                    <div className={styles.cardBlurb}>{g.blurb}</div>
                  </button>
                );
              })}
            </div>

            {isEntryA && onSkipForNow && (
              <div className={styles.rowRight}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={onSkipForNow}
                >
                  Skip for Now
                </button>
              </div>
            )}
          </section>

          {goalKey && (
            <section className={styles.panel}>
              <div className={styles.panelHead}>
                <div className={styles.panelTitle}>{section2Title}</div>
                <div className={styles.panelSub}>{section2Sub}</div>
              </div>

              {goalKey === 'fill_slow_periods' && (
                <div className={styles.softBox}>
                  <div className={styles.softTitle}>
                    When are your slow periods? (optional)
                  </div>

                  <div className={styles.softRow}>
                    <div className={styles.softLabel}>Days</div>
                    <div className={styles.chips}>
                      {DAYS.map(d => {
                        const active = slowDays.includes(d);
                        return (
                          <button
                            key={d}
                            type="button"
                            className={`${styles.chip} ${
                              active ? styles.chipActive : ''
                            }`}
                            onClick={() => toggleSlowDay(d)}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className={styles.softRow}>
                    <div className={styles.softLabel}>Time blocks</div>
                    <div className={styles.chips}>
                      {TIME_BLOCKS.map(t => {
                        const active = slowTimes.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            className={`${styles.chip} ${
                              active ? styles.chipActive : ''
                            }`}
                            onClick={() => toggleSlowTime(t)}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {goalKey === 'fill_slow_periods' && (
                <div className={styles.subSectionHead}>
                  <div className={styles.subSectionTitle}>
                    What’s Your Core Offering?
                  </div>
                  <div className={styles.subSectionSub}>
                    Help us understand your core product or service to create a
                    solution that brings customers in during quieter times.
                  </div>
                </div>
              )}

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>Item type</label>
                  <div className={styles.typeRow}>
                    {ITEM_TYPES.map((it, idx) => {
                      const active = itemType === it.key;
                      return (
                        <button
                          key={it.key}
                          type="button"
                          className={`${styles.typeBtn} ${
                            active ? styles.typeActive : ''
                          }`}
                          onClick={() => setItemType(it.key)}
                          aria-pressed={active}
                          style={{ '--stagger': `${idx * 60}ms` }}
                        >
                          <div className={styles.typeLabel}>{it.label}</div>
                          <div className={styles.typeHint}>{it.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {itemType && (
                  <>
                    <div className={styles.field}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                        }}
                      >
                        <label className={styles.label}>
                          {itemType === 'individual'
                            ? 'Title (Main Item)'
                            : itemType === 'category'
                            ? 'Title (Line of Items)'
                            : 'Title (Storewide)'}
                        </label>

                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => handleGenerateTitle(values)}
                          disabled={!canGenerateTitle || isGenTitleLoading}
                          aria-disabled={!canGenerateTitle || isGenTitleLoading}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 999,
                            fontSize: 12.5,
                            fontWeight: 900,
                            opacity: !canGenerateTitle ? 0.6 : 1,
                          }}
                        >
                          {isGenTitleLoading ? 'Generating…' : 'Generate'}
                        </button>
                      </div>

                      <input
                        className={styles.input}
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder={
                          itemType === 'individual'
                            ? 'e.g., Espresso Latte'
                            : itemType === 'category'
                            ? 'e.g., Lunch Combos'
                            : 'e.g., Everything in Store'
                        }
                      />
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label}>
                        Category (recommended)
                      </label>
                      <Field name="category">
                        {({ input, meta }) => (
                          <CouponCategoryField
                            input={input}
                            meta={meta}
                            label="Select Category"
                          />
                        )}
                      </Field>
                    </div>

                    <div className={styles.field}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                        }}
                      >
                        <label className={styles.label}>
                          Description (optional)
                        </label>

                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => handleGenerateDescription(values)}
                          disabled={!canGenerateDesc || isGenDescLoading}
                          aria-disabled={!canGenerateDesc || isGenDescLoading}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 999,
                            fontSize: 12.5,
                            fontWeight: 900,
                            opacity: !canGenerateDesc ? 0.6 : 1,
                          }}
                        >
                          {isGenDescLoading ? 'Generating…' : 'Generate'}
                        </button>
                      </div>

                      <textarea
                        className={styles.textarea}
                        rows={3}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Write a rough description, or click Generate to get one."
                      />
                    </div>

                    <div className={styles.twoCol}>
                      <div className={styles.field}>
                        <label className={styles.label}>Price (optional)</label>
                        <input
                          className={styles.input}
                          type="number"
                          min="0"
                          step="0.01"
                          value={price}
                          onChange={e => setPrice(e.target.value)}
                          placeholder="e.g., 9.99"
                        />
                      </div>

                      <div className={styles.field}>
                        <label className={styles.label}>
                          Purchase Quantity (optional)
                        </label>
                        <input
                          className={styles.input}
                          type="number"
                          min="1"
                          step="1"
                          value={purchaseQty}
                          onChange={e => setPurchaseQty(e.target.value)}
                          placeholder="e.g., 2"
                        />
                      </div>
                    </div>

                    <div className={styles.twoCol}>
                      <div className={styles.field}>
                        <label className={styles.label}>
                          Minimum Spend (optional)
                          {itemType === 'individual' && (
                            <span className={styles.badge}>
                              Disabled for Individual Item
                            </span>
                          )}
                        </label>
                        <input
                          className={styles.input}
                          type="number"
                          min="0"
                          step="0.01"
                          value={minSpend}
                          onChange={e => setMinSpend(e.target.value)}
                          placeholder="e.g., 50.00"
                          disabled={itemType === 'individual'}
                        />
                      </div>

                      <div className={styles.field}>
                        <label className={styles.label}> </label>
                        <div />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          )}

          {goalKey && canRevealImpact && (
            <section className={styles.panel}>
              <div className={styles.panelHead}>
                <div className={styles.panelTitle}>
                  How do you want your offer to perform?
                </div>
                <div className={styles.panelSub}>
                  This helps us tailor the impact of your solution to best
                  achieve your goals.
                </div>
              </div>

              <div
                className={styles.radioGrid}
                role="radiogroup"
                aria-label="Desired offer impact"
              >
                {IMPACTS.map((i, idx) => {
                  const active = impactKey === i.key;
                  return (
                    <button
                      key={i.key}
                      type="button"
                      className={`${styles.radioCard} ${
                        active ? styles.active : ''
                      }`}
                      role="radio"
                      aria-checked={active}
                      onClick={() => setImpactKey(i.key)}
                      style={{ '--stagger': `${idx * 60}ms` }}
                    >
                      <div className={styles.cardTop}>
                        <div className={styles.cardLabel}>{i.label}</div>
                      </div>
                      <div className={styles.cardBlurb}>{i.blurb}</div>
                    </button>
                  );
                })}
              </div>

              <div className={styles.ctaWrap}>
                <div className={styles.ctaHint}>
                  Ready to see the custom coupons our AI has built just for you,
                  based on your goals and preferences?
                </div>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={!canGenerate}
                  aria-disabled={!canGenerate}
                  onClick={() => submitForm()}
                >
                  Generate My Personalized Coupons!
                </button>
              </div>
            </section>
          )}

          {goalObj && (
            <div className={styles.note}>
              <strong>Selected goal:</strong> {goalObj.label}
            </div>
          )}
        </div>
      )}
    </Form>
  );
}

Step1_CombinedSetup.propTypes = {
  entryPoint: PropTypes.oneOf(['A', 'B']),
  draft: PropTypes.object.isRequired,
  onGenerate: PropTypes.func.isRequired,
  onSkipForNow: PropTypes.func,
  onPreviewChange: PropTypes.func,
  onGenerateTitle: PropTypes.func,
  onGenerateDescription: PropTypes.func,
};
